<template>
  <div class="header-container">
    <!-- 背景遮罩层，仅在手机模式下菜单打开时显示 -->
    <div class="menu-overlay" v-if="isMobile && isNavOpen" @click="toggleMenu"></div>
    <div class="header-content">
      <!-- 左侧logo -->
      <div class="logo-container">
        <img src="../assets/logo.png" alt="Dr.Motor Logo" class="logo-image" @click="goToHome">
      </div>

      <!-- 移动端语言选择器 (仅在小屏幕显示) -->
      <div class="mobile-language-selector" v-if="isMobile">
        <div class="language-selector-wrapper">
          <div class="language-dropdown-container">
            <!-- 添加语言图标 -->
            <img src="../assets/language.png" alt="Language" class="language-dropdown-icon">
            <el-select v-model="locale" size="small" class="custom-language-select">
              <el-option value="zh-Hant">
                <div class="language-option">
                  <span class="language-icon">🇹🇼</span>
                  <span class="language-text">繁中</span>
                </div>
              </el-option>
              <el-option value="en">
                <div class="language-option">
                  <span class="language-icon">🇬🇧</span>
                  <span class="language-text">English</span>
                </div>
              </el-option>
            </el-select>
          </div>
        </div>
      </div>

      <!-- 汉堡菜单按钮 (仅在小屏幕显示) -->
      <div class="menu-toggle" @click="toggleMenu" v-if="isMobile">
        <div class="menu-icon">
          <span class="menu-line"></span>
          <span class="menu-line"></span>
          <span class="menu-line"></span>
        </div>
      </div>

      <!-- 右侧导航链接和语言切换 -->
      <div class="nav-container" :class="{ 'nav-open': isNavOpen }" style="z-index: 9999;">
        <router-link to="/" class="nav-item" active-class="active" exact>
          {{ t('home') }}
        </router-link>

        <!-- 产品分类下拉菜单 -->
        <div class="nav-item dropdown"
             @mouseenter="openDropdown('product')"
             @mouseleave="closeDropdown('product')"
             :class="{ 'active': isProductRouteActive }">
          <div class="dropdown-toggle" @click="toggleDropdown('product')">
            {{ t('productCategory') }}
            <span class="dropdown-arrow">▼</span>
          </div>
          <div class="dropdown-menu" v-show="dropdownOpen.product">
            <!-- 动态生成产品分类菜单 -->
            <template v-for="category in productCategories" :key="category.id">
              <!-- 处理带有子菜单的一级分类 -->
              <div v-if="category.children && category.children.length > 0" class="dropdown-submenu">
                <div class="dropdown-submenu-toggle"
                     @click="handleParentCategoryClick(category)"
                     :class="{ 'active': route.query.topicId === category.id.toString() }">
                  {{ locale === 'en' ? category.menuNameEn : category.menuName }}
                  <span class="dropdown-submenu-arrow">▶</span>
                </div>
                <!-- 二级菜单内容 -->
                <div class="dropdown-submenu-content">
                  <!-- 修改路由链接，使用动态类来控制高亮状态 -->
                  <router-link
                      v-for="subCategory in category.children"
                      :key="subCategory.id"
                      :to="{ name: 'TopicDetail', query: { topicId: subCategory.id, from: 'category' } }"
                      class="dropdown-item"
                      :class="{ 'active': route.query.topicId === subCategory.id.toString() }"
                      @click="handleCategoryClick"
                  >
                    {{ locale === 'en' ? subCategory.menuNameEn : subCategory.menuName }}
                  </router-link>
                </div>
              </div>
              <!-- 处理没有子菜单的分类 -->
              <router-link
                  v-else
                  :to="{ name: 'TopicDetail', query: { topicId: category.id, from: 'category' } }"
                  class="dropdown-item"
                  :class="{ 'active': route.query.topicId === category.id.toString() }"
                  @click="handleCategoryClick"
              >
                {{ locale === 'en' ? category.menuNameEn : category.menuName }}
              </router-link>
            </template>
          </div>
        </div>

        <router-link to="/contact" class="nav-item" active-class="active" exact>
          {{ t('contactUs') }}
        </router-link>

        <!-- 语言切换下拉列表 - 仅在桌面端显示 -->
        <div class="language-selector" v-if="!isMobile">
          <div class="language-dropdown-container">
            <!-- 添加语言图标 -->
            <img src="../assets/language.png" alt="Language" class="language-dropdown-icon">
            <!-- 下拉列表 -->
            <select 
              v-model="locale" 
              class="language-dropdown"
            >
              <option value="zh-Hant">
                <span class="language-icon">🇹🇼</span> 繁中
              </option>
              <option value="en">
                <span class="language-icon">🇬🇧</span> English
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, onMounted, onUnmounted, reactive, computed} from 'vue';
import {RouterLink, onBeforeRouteLeave, useRouter, useRoute} from 'vue-router';
import {useI18n} from 'vue-i18n';
import './../styles/GloablHeader.scss';

// 使用i18n
const {locale, t} = useI18n();

// 控制导航菜单的显示状态
const isNavOpen = ref(false);
// 判断是否为移动设备
const isMobile = ref(false);
// 控制下拉菜单的显示状态
const dropdownOpen = reactive({
  product: false
});

// 存储产品分类数据
interface Category {
  id: number;
  parentId: number;
  menuName: string;
  menuNameEn: string;
  menuOrder: number;
  children: Category[] | null;
}

const productCategories = ref<Category[]>([]);

// 获取路由实例和当前路由
const router = useRouter();
const route = useRoute();

// 添加导航到首页的函数
function goToHome() {
  router.push('/');
}

// 计算属性：检测当前路由是否为产品分类下的路由
const isProductRouteActive = computed(() => {
  const currentRoute = route.path;
  // 检查当前路由是否包含产品ID路径
  return currentRoute.startsWith('/product/') ||
      ['/electric-motorcycles', '/motorcycle-parts', '/battery-exchange', '/ev-chargers', '/ev-cables', '/ev-adapters'].includes(currentRoute);
});

// 获取产品分类数据
async function fetchProductCategories() {
  try {
    // const response = await fetch('http://172.16.10.48:8080/jeecg-boot/drmotoev/menu/get');
    const response = await fetch('/jeecg-boot/drmotoev/menu/get');

    const data = await response.json();
    if (data.code === 200 && data.result) {
      // 根据menuOrder排序
      productCategories.value = data.result.sort((a: Category, b: Category) => a.menuOrder - b.menuOrder);
    }
    console.log("~~~~~~~~~~~~~~~productCategories：", productCategories.value)
  } catch (error) {
    console.error('获取产品分类数据失败:', error);
    // 为了演示效果，添加模拟数据（根据用户提供的接口示例更新）
    productCategories.value = [
      {
        id: 1,
        parentId: 0,
        menuName: "AA",
        menuNameEn: "aa",
        menuOrder: 0,
        children: [
          {
            id: 2,
            parentId: 1,
            menuName: "SS",
            menuNameEn: "ss",
            menuOrder: 1,
            children: null
          }
        ]
      },
      {
        id: 3,
        parentId: 0,
        menuName: "DD",
        menuNameEn: "dd",
        menuOrder: 1,
        children: null
      }
    ];
  }
}

// 切换导航菜单显示/隐藏
function toggleMenu() {
  if (isNavOpen.value) {
    isNavOpen.value = false;

    // 在动画结束后再重置其他状态
    setTimeout(() => {
      // 仅在非移动设备上重置下拉菜单状态
      if (!isMobile.value) {
        Object.keys(dropdownOpen).forEach(key => {
          dropdownOpen[key as keyof typeof dropdownOpen] = false;
        });
      }
    }, 400);
  } else {
    // 打开菜单时的逻辑
    isNavOpen.value = true;

    // 在移动设备上，当菜单打开时自动展开所有下拉菜单
    if (isMobile.value) {
      Object.keys(dropdownOpen).forEach(key => {
        dropdownOpen[key as keyof typeof dropdownOpen] = true;
      });
    }
  }
}

// 打开下拉菜单
function openDropdown(type: string) {
  dropdownOpen[type as keyof typeof dropdownOpen] = true;
}

// 关闭下拉菜单
function closeDropdown(type: string) {
  // 在移动设备上不允许关闭下拉菜单
  if (!isMobile.value) {
    setTimeout(() => {
      dropdownOpen[type as keyof typeof dropdownOpen] = false;
    }, 200);
  }
}
    // 处理带有子菜单的一级分类点击事件
    function handleParentCategoryClick(category: Category) {
      // 导航到一级分类自身的内容
      router.push({
        name: 'TopicDetail', 
        query: {
          topicId: category.id,  // 使用一级分类的ID
          from: 'category',
          isParentCategory: 'true'  // 添加标记，表示这是从一级分类点击过来的
        }
      });
      handleCategoryClick(); // 调用通用的分类点击处理逻辑
    }

// 切换下拉菜单状态 - 修复：移除移动设备限制
function toggleDropdown(type: string) {
  dropdownOpen[type as keyof typeof dropdownOpen] = !dropdownOpen[type as keyof typeof dropdownOpen];
}

// 处理分类点击事件，确保导航后菜单状态正确
function handleCategoryClick() {
  if (!isMobile.value) {
    // 在桌面设备上点击分类后关闭下拉菜单
    dropdownOpen.product = false;
  }
  // 在移动设备上保持菜单打开状态
}

// 检查屏幕尺寸，更新isMobile状态
function checkScreenSize() {
  isMobile.value = window.innerWidth <= 768;
  // 如果窗口宽度大于768px，强制关闭导航菜单和下拉菜单
  if (window.innerWidth > 768) {
    isNavOpen.value = false;
    Object.keys(dropdownOpen).forEach(key => {
      dropdownOpen[key as keyof typeof dropdownOpen] = false;
    });
    document.body.style.overflow = '';
  }
}

// 在路由离开前关闭菜单
onBeforeRouteLeave((to, from, next) => {
  if (isNavOpen.value) {
    isNavOpen.value = false;
    document.body.style.overflow = '';

    // 在非移动设备上也重置下拉菜单状态
    Object.keys(dropdownOpen).forEach(key => {
      dropdownOpen[key as keyof typeof dropdownOpen] = false;
    });
  }
  next();
});

// 处理点击其他区域关闭菜单 - 修复类型注解
function handleClickOutside(event: MouseEvent): void {
  const target = event.target as HTMLElement;
  const menuContainer = document.querySelector('.nav-container') as HTMLElement | null;
  const menuToggle = document.querySelector('.menu-toggle') as HTMLElement | null;
  
  // 检查是否点击在菜单或菜单按钮外
  if (menuContainer && menuToggle && 
      !menuContainer.contains(target) && 
      !menuToggle.contains(target)) {
    
    // 关闭导航菜单
    if (isNavOpen.value) {
      isNavOpen.value = false;
      
      // 重置下拉菜单状态
      setTimeout(() => {
        Object.keys(dropdownOpen).forEach(key => {
          dropdownOpen[key as keyof typeof dropdownOpen] = false;
        });
        document.body.style.overflow = '';
      }, 400);
    } else {
      // 如果菜单已经关闭，只重置下拉菜单
      Object.keys(dropdownOpen).forEach(key => {
        dropdownOpen[key as keyof typeof dropdownOpen] = false;
      });
    }
  }
}

// 定义事件处理函数引用
let clickOutsideHandler: (event: MouseEvent) => void;
let resizeHandler: () => void;
let routeChangeHandler: () => void;
let removeRouteListener: () => void;

// 监听窗口大小变化和路由变化
onMounted(() => {
  // 初始化处理函数
  clickOutsideHandler = handleClickOutside;
  resizeHandler = checkScreenSize;
  routeChangeHandler = () => {
    if (isNavOpen.value) {
      isNavOpen.value = false;
      document.body.style.overflow = '';

      // 重置下拉菜单状态
      Object.keys(dropdownOpen).forEach(key => {
        dropdownOpen[key as keyof typeof dropdownOpen] = false;
      });
    }
  };
  
  checkScreenSize();
  window.addEventListener('resize', resizeHandler);
  document.addEventListener('click', clickOutsideHandler);

  // 获取产品分类数据
  fetchProductCategories();

  // 添加路由变化监听
  removeRouteListener = router.afterEach(routeChangeHandler);
});

// 组件卸载时移除事件监听 - 修复事件监听移除逻辑
onUnmounted(() => {
  window.removeEventListener('resize', resizeHandler);
  document.removeEventListener('click', clickOutsideHandler);
  document.body.style.overflow = '';
  
  // 移除路由监听器
  if (removeRouteListener) {
    removeRouteListener();
  }
});
</script>

<style scoped>
/* 语言下拉列表样式 */
.language-dropdown-container {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}

.language-dropdown-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  vertical-align: middle;
}

.language-dropdown {
  padding: 8px 30px 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  position: relative;
  transition: all 0.3s ease;
}

.language-dropdown:hover {
  border-color: rgba(255, 255, 255, 0.6);
  background-color: rgba(0, 0, 0, 0.9);
}

.language-dropdown:focus {
  outline: none;
  border-color: #12aa4a;
  box-shadow: 0 0 0 2px rgba(18, 170, 74, 0.2);
}

/* 下拉箭头样式 */
.language-dropdown-container::after {
  content: '▼';
  color: white;
  font-size: 10px;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

/* 选项样式 */
.language-dropdown option {
  background-color: #333;
  color: white;
  padding: 8px;
}

/* 确保图标和文字对齐 */
.language-icon {
  font-size: 16px;
}

.language-text {
  font-size: 14px;
}
</style>
